import * as React from 'react';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import * as Device from 'react-device-detect';

import { useSession, useToastError, usePushNotifications } from '@hooks';
import { conversationIdVar } from '@context';
import {
  useGetConversationQuery,
  useOnMessageSentSubscription,
  useResetUnreadCountMutation,
  ListConversationsQuery,
  ListConversationsDocument,
} from '@graphql';
import { Message } from './Message';
import { AddMessage } from './AddMessage';

const GROUP_WINDOW_MS = 60_000;
const MESSAGES_PAGE_SIZE = 30;

export function Conversation(props: Readonly<{ conversationId: string }>) {
  const { conversationId } = props;
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const [composerHeight, setComposerHeight] = React.useState(0);
  const { user, account } = useSession();
  const { clearConversationNotifications } = usePushNotifications();
  const toastError = useToastError();

  const [resetUnreadCount] = useResetUnreadCountMutation({
    variables: { conversationId },
    onError: toastError,
  });

  const { data, updateQuery, client, loading, fetchMore } = useGetConversationQuery({
    skip: !user || !conversationId,
    variables: { conversationId },
    onError: toastError,
    onCompleted: ({ conversation }) => {
      if (conversation) {
        resetUnreadCount({
          onCompleted: () => {
            client.cache.updateQuery({ query: ListConversationsDocument }, (cachedData) => {
              if (!cachedData) return cachedData;

              return {
                conversations: cachedData.conversations.map(
                  (conversation: ListConversationsQuery['conversations'][0]) =>
                    conversation.id === conversationId ? { ...conversation, unreadCount: 0 } : conversation
                ),
              };
            });
          },
        });
      }
    },
  });

  useOnMessageSentSubscription({
    variables: { conversationId },
    onError: toastError,
    onData: ({ data: subData }) => {
      const edge = subData?.data?.messageSent;
      if (!edge) return;

      const newMessage = edge.node;

      updateQuery((prev) => {
        if (!prev.conversation) return prev;

        const updatedConversation = {
          ...prev.conversation,
          messages: {
            ...prev.conversation.messages,
            // ici on pousse l'EDGE, pas le node
            edges: [edge, ...prev.conversation.messages.edges],
          },
        };

        resetUnreadCount({
          onCompleted: () => {
            client.cache.updateQuery({ query: ListConversationsDocument }, (cachedData) => {
              if (!cachedData) return cachedData;

              return {
                conversations: cachedData.conversations.map(
                  (conversation: ListConversationsQuery['conversations'][0]) =>
                    conversation.id === conversationId
                      ? {
                          ...conversation,
                          unreadCount: 0,
                          // lastMessage est un Message → on met le node
                          lastMessage: newMessage,
                        }
                      : conversation
                ),
              };
            });
          },
        });

        return {
          ...prev,
          conversation: updatedConversation,
        };
      });
    },
  });

  const [infiniteRef] = useInfiniteScroll({
    loading: isFetchingMore,
    hasNextPage: Boolean(data?.conversation?.messages.pageInfo.hasNextPage),
    disabled: !data?.conversation?.messages.pageInfo.hasNextPage,
    // Trigger load when the sentry is near the viewport
    rootMargin: '0px 0px 400px 0px',
    onLoadMore: () => {
      if (!data?.conversation?.messages.pageInfo.hasNextPage) return;
      const endCursor = data.conversation.messages.pageInfo.endCursor;
      if (!endCursor) return;

      setIsFetchingMore(true);

      fetchMore({
        variables: {
          conversationId,
          after: endCursor,
          first: MESSAGES_PAGE_SIZE, // keep same page size
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          setIsFetchingMore(false);

          if (!fetchMoreResult?.conversation) return prev;

          const prevConversation = prev.conversation;
          const moreConversation = fetchMoreResult.conversation;

          if (!prevConversation) return prev;

          const mergedMessages = {
            ...moreConversation.messages,
            // IMPORTANT: messages triés desc (newest first), on ajoute les plus anciens à la fin
            edges: [...prevConversation.messages.edges, ...moreConversation.messages.edges],
          };

          const mergedConversation = {
            ...prevConversation,
            messages: mergedMessages,
          };

          return {
            ...prev,
            conversation: mergedConversation,
          };
        },
      }).catch((err) => {
        setIsFetchingMore(false);
        toastError(err);
      });
    },
  });

  // important to set and unset the reactive var to keep track of the currently open conversation
  // it will be used in the push notification hook to ignore notifications for the open conversation
  React.useEffect(() => {
    conversationIdVar(conversationId);

    // clear notifications related to this conversation when it becomes active
    clearConversationNotifications(conversationId).catch((err) => {
      if (import.meta.env.DEV) {
        console.warn('[NOTIFS] clearConversationNotifications from Conversation error', err);
      }
    });

    return () => {
      conversationIdVar(null);
    };
  }, [conversationId]);

  if (loading) {
    return (
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={40} color="uncolored" />
      </DialogContent>
    );
  }

  if (data?.conversation) {
    return (
      <Stack
        px={2}
        position="relative"
        flex={1}
        minHeight={0}
        sx={{
          borderBottomLeftRadius: '25px',
          borderBottomRightRadius: '25px',
          overflow: 'hidden',
        }}
      >
        <Stack
          minHeight={0}
          flex={1}
          direction="column-reverse"
          pt={2}
          pb={`${composerHeight}px`}
          mb={1.5}
          sx={{
            gap: 0.3,
            overflowY: 'auto',
            borderBottomLeftRadius: '25px',
            borderBottomRightRadius: '25px',
          }}
        >
          {data.conversation.messages.edges.map((message, idx, arr) => {
            const top = arr[idx + 1]; // visuellement au-dessus (plus ancien)
            const bottom = arr[idx - 1]; // visuellement en-dessous (plus récent)

            const sameSenderTop = !!top && top.node.sender.id === message.node.sender.id;
            const sameSenderBottom = !!bottom && bottom.node.sender.id === message.node.sender.id;

            const closeTop =
              !!top &&
              Math.abs(new Date(message.node.createdAt).getTime() - new Date(top.node.createdAt).getTime()) <
                GROUP_WINDOW_MS;

            const closeBottom =
              !!bottom &&
              Math.abs(
                new Date(message.node.createdAt).getTime() - new Date(bottom.node.createdAt).getTime()
              ) < GROUP_WINDOW_MS;

            const groupedTop = sameSenderTop && closeTop; // voisin du haut fusionné
            const groupedBottom = sameSenderBottom && closeBottom; // voisin du bas fusionné

            // avatar + timestamp uniquement sur le DERNIER du groupe (pas de voisin en-dessous)
            const showMeta = !groupedBottom;

            const addSeparator =
              // is last read message
              message.node.id === data.conversation.lastReadMessageId &&
              // and has more than 5 messages above
              idx > 4 &&
              // and it's not one of my messages
              message.node.sender.id !== account?.id;

            const addStartOfConversation =
              !data.conversation.messages.pageInfo.hasNextPage &&
              idx > MESSAGES_PAGE_SIZE &&
              idx === arr.length - 1;

            return (
              <Message
                key={message.node.id}
                message={message.node}
                members={data.conversation.members}
                groupedTop={groupedTop}
                groupedBottom={groupedBottom}
                showMeta={showMeta}
                addSeparator={addSeparator}
                addStartOfConversation={addStartOfConversation}
                sx={{ pt: arr.length - 1 === idx ? (Device.isDesktop ? 2 : 2) : 0 }}
              />
            );
          })}

          {data.conversation.messages.pageInfo.hasNextPage && (
            <div ref={infiniteRef} style={{ padding: '40px 0', textAlign: 'center' }}>
              {isFetchingMore && <CircularProgress size={24} color="uncolored" />}
            </div>
          )}
        </Stack>
        <AddMessage setComposerHeight={setComposerHeight} />
      </Stack>
    );
  }

  return null;
}
